import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import { QueryState } from "@/shared/ui/QueryState";
import {
  Badge,
  Button,
  Card,
  CardContent,
  cn,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  RadioGroup,
  RadioGroupItem,
  RHF,
  Separator,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  ListChecks,
  MessageSquareText,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import { z } from "zod";
import type {
  InspectorStandardDetailDTO,
  InspectorStandardItemDTO,
  WorkItemTypeDTO,
} from "../api/client";
import {
  useCreateQcRecordMutation,
  useInspectorStandardDetailQuery,
  useWorkItemTypesQuery,
} from "../api/queries";

const QC_RECORD_IMAGE_PREFIX = "uploads/qc-record";

export type QcInspectionFormValues = {
  workItemId: string;
  workItemTypeId: string;
  location: string;
  notes: string;
  checklist: {
    qcStandardItemId: string;
    status: "pass" | "fail";
    notes: string;
    photoUrl: string;
    /** ເລືອກແລ້ວ ຍັງບໍ່ອັບ — ອັບເມື່ອ submit */
    photoFile: File | null;
  }[];
};

function buildQcInspectionSchema(detail: InspectorStandardDetailDTO) {
  return z
    .object({
      workItemId: z.string(),
      workItemTypeId: z.string(),
      location: z.string(),
      notes: z.string(),
      checklist: z.array(
        z.object({
          qcStandardItemId: z.string(),
          status: z.enum(["pass", "fail"]),
          notes: z.string(),
          photoUrl: z.string(),
          photoFile: z.union([z.instanceof(File), z.null()]),
        }),
      ),
    })
    .superRefine((data, ctx) => {
      if (data.checklist.length !== detail.items.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["checklist"],
          message: "ລາຍການກວດບໍ່ຄົບ",
        });
      }
      data.checklist.forEach((row, i) => {
        if (row.status === "fail" && detail.requireNotesOnFail) {
          if (!row.notes?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["checklist", i, "notes"],
              message: "ຕ້ອງໃສ່ເຫດຜົນເມື່ອບໍ່ຜ່ານ",
            });
          }
        }
        if (row.status === "fail" && detail.requirePhoto) {
          const hasPhoto =
            Boolean(row.photoUrl?.trim()) || row.photoFile instanceof File;
          if (!hasPhoto) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["checklist", i, "photoUrl"],
              message: "ຕ້ອງແນບຮູບເມື່ອບໍ່ຜ່ານ",
            });
          }
        }
      });
    });
}

function defaultValuesForDetail(
  detail: InspectorStandardDetailDTO,
): QcInspectionFormValues {
  return {
    workItemId: "",
    workItemTypeId: "",
    location: "",
    notes: "",
    checklist: detail.items.map((it) => ({
      qcStandardItemId: it.id,
      status: "pass",
      notes: "",
      photoUrl: "",
      photoFile: null,
    })),
  };
}

function QcChecklistItemFields({
  index,
  itemMeta,
  detail,
}: {
  index: number;
  itemMeta: InspectorStandardItemDTO;
  detail: InspectorStandardDetailDTO;
}) {
  const { control } = RHF.useFormContext<QcInspectionFormValues>();
  const status = useWatch({
    control,
    name: `checklist.${index}.status`,
  }) as "pass" | "fail" | undefined;
  const failed = status === "fail";

  const showFailDetails =
    failed && (detail.requireNotesOnFail || detail.requirePhoto);

  return (
    <Card className="overflow-hidden border-border/80 py-0 shadow-sm transition-shadow duration-200 hover:shadow-md">
      <CardContent className="p-0">
        <div className="flex gap-4 px-5 pt-5 pb-4">
          <div
            className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted font-semibold text-muted-foreground text-sm tabular-nums"
            aria-hidden
          >
            {index + 1}
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-start gap-2">
              <div className="flex min-w-0 items-start gap-2">
                <ListChecks
                  className="mt-0.5 size-4 shrink-0 text-primary/80"
                  aria-hidden
                />
                <h3 className="font-semibold text-base text-foreground leading-snug tracking-tight">
                  {itemMeta.title}
                </h3>
              </div>
              {itemMeta.isRequired ? (
                <Badge
                  variant="outline"
                  className="shrink-0 border-destructive/35 text-[0.65rem] text-destructive uppercase tracking-wide"
                >
                  ຈຳເປັນ
                </Badge>
              ) : null}
            </div>
            {itemMeta.description ? (
              <p className="rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-muted-foreground text-sm leading-relaxed">
                {itemMeta.description}
              </p>
            ) : null}
          </div>
        </div>

        <Separator className="bg-border/70" />

        <RHF.Controller
          name={`checklist.${index}.status`}
          render={({ field }) => (
            <div className="space-y-3 bg-muted/15 px-5 py-4">
              <p className="font-medium text-[0.7rem] text-muted-foreground uppercase tracking-wider">
                ຜົນກວດ
              </p>
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="grid gap-3 sm:grid-cols-2"
              >
                <label
                  htmlFor={`qc-pass-${index}`}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3.5 text-sm transition-all duration-200",
                    field.value === "pass"
                      ? "border-primary bg-primary/10 shadow-sm ring-2 ring-primary/25"
                      : "border-border/80 bg-background hover:border-primary/35 hover:bg-muted/40",
                  )}
                >
                  <CheckCircle2
                    className={cn(
                      "size-5 shrink-0",
                      field.value === "pass"
                        ? "text-primary"
                        : "text-muted-foreground/70",
                    )}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1 font-semibold">ຜ່ານ</span>
                  <RadioGroupItem
                    value="pass"
                    id={`qc-pass-${index}`}
                    className="shrink-0"
                  />
                </label>
                <label
                  htmlFor={`qc-fail-${index}`}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3.5 text-sm transition-all duration-200",
                    field.value === "fail"
                      ? "border-destructive bg-destructive/10 shadow-sm ring-2 ring-destructive/25"
                      : "border-border/80 bg-background hover:border-destructive/40 hover:bg-muted/40",
                  )}
                >
                  <XCircle
                    className={cn(
                      "size-5 shrink-0",
                      field.value === "fail"
                        ? "text-destructive"
                        : "text-muted-foreground/70",
                    )}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1 font-semibold">ບໍ່ຜ່ານ</span>
                  <RadioGroupItem
                    value="fail"
                    id={`qc-fail-${index}`}
                    className="shrink-0"
                  />
                </label>
              </RadioGroup>
            </div>
          )}
        />

        {showFailDetails ? (
          <>
            <Separator className="bg-border/70" />
            <div className="space-y-4 border-destructive/10 border-t bg-destructive/4 px-5 py-4">
              <div className="flex items-center gap-2 text-destructive">
                <MessageSquareText className="size-4 shrink-0" aria-hidden />
                <p className="font-medium text-[0.7rem] uppercase tracking-wider">
                  ລາຍລະອຽດເມື່ອບໍ່ຜ່ານ
                </p>
              </div>
              <div className="space-y-4">
                {detail.requireNotesOnFail ? (
                  <FormTextarea
                    label="ເຫດຜົນ"
                    rows={2}
                    placeholder="ເຫດຜົນ"
                    name={`checklist.${index}.notes`}
                    requiredMark
                  />
                ) : null}
                {detail.requirePhoto ? (
                  <RHF.Controller
                    name={`checklist.${index}.photoUrl`}
                    render={({ field: urlField }) => (
                      <RHF.Controller
                        name={`checklist.${index}.photoFile`}
                        render={({ field: fileField }) => (
                          <ImageKeyUploadField
                            value={urlField.value ?? ""}
                            onChange={urlField.onChange}
                            deferUpload
                            pendingFile={fileField.value ?? null}
                            onFileSelect={(f) => fileField.onChange(f)}
                            onClear={() => {
                              urlField.onChange("");
                              fileField.onChange(null);
                            }}
                            keyPrefix={QC_RECORD_IMAGE_PREFIX}
                            label="ຮູບ (ເມື່ອບໍ່ຜ່ານ)"
                            aspectRatio="aspect-video"
                          />
                        )}
                      />
                    )}
                  />
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function QcInspectionFormInner({
  detail,
  workTypes,
}: {
  detail: InspectorStandardDetailDTO;
  workTypes: WorkItemTypeDTO[];
}) {
  const navigate = useNavigate({ from: "/app/qc/inspection/$standardId" });
  const createMutation = useCreateQcRecordMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = useMemo(() => buildQcInspectionSchema(detail), [detail]);

  const methods = RHF.useForm<QcInspectionFormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultValuesForDetail(detail),
  });

  const { fields } = useFieldArray({
    control: methods.control,
    name: "checklist",
  });

  const submitBusy = isSubmitting || createMutation.isPending;

  const onSubmit = async (values: QcInspectionFormValues) => {
    setIsSubmitting(true);
    try {
      const items: {
        qcStandardItemId: string;
        status: "pass" | "fail";
        notes: string | null;
        photoUrl: string | null;
      }[] = [];

      for (const row of values.checklist) {
        let photoUrl: string | null = null;
        if (row.status === "fail") {
          if (row.photoFile instanceof File) {
            photoUrl = await uploadImageToKey(
              QC_RECORD_IMAGE_PREFIX,
              row.photoFile,
            );
          } else {
            photoUrl = row.photoUrl.trim() || null;
          }
        }

        items.push({
          qcStandardItemId: row.qcStandardItemId,
          status: row.status,
          notes: row.notes.trim() || null,
          photoUrl,
        });
      }

      await createMutation.mutateAsync({
        qcStandardId: detail.id,
        workItemId: values.workItemId.trim() || null,
        workItemTypeId: values.workItemTypeId.trim() || null,
        notes: values.notes.trim() || null,
        location: values.location.trim() || null,
        items,
      });

      toast.success("ບັນທຶກການກວດສຳເລັດ");
      navigate({
        to: "/app/qc/inspection",
        search: { page: 1 },
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ບັນທຶກບໍ່ສຳເລັດ");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="mb-6 flex justify-between gap-4">
        <div>
          <h1 className="font-bold text-2xl tracking-tight">{detail.name}</h1>
          {detail.description ? (
            <p className="mt-1 text-muted-foreground">{detail.description}</p>
          ) : (
            <p className="mt-1 text-muted-foreground">
              ບັນທຶກຜົນກວດຕາມມາດຕະຖານ — {detail.workTypeName}
              {detail.workStage ? ` · ${detail.workStage}` : ""}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-start gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              navigate({ to: "/app/qc/inspection", search: { page: 1 } })
            }
          >
            <ArrowLeft className="mr-1 size-4" /> ກັບຄືນ
          </Button>
        </div>
      </div>

      <div className="mx-auto w-full max-w-2xl space-y-6">
        <Card>
          <CardContent>
            <FormRoot<QcInspectionFormValues>
              methods={methods}
              onSubmit={onSubmit}
              className="w-full space-y-6"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <FormInput
                  label="ລະຫັດງານທີ່ກວດ (ຖ້າມີ)"
                  placeholder="work_item_id"
                  name="workItemId"
                />

                <FormSelect
                  label="ປະເພດງານ"
                  name="workItemTypeId"
                  options={workTypes.map((t) => ({
                    label: t.name,
                    value: t.id,
                  }))}
                />
              </div>

              <FormInput
                placeholder="ສະຖານທີ່ກວດ"
                name="location"
                label="ສະຖານທີ່ກວດ"
              />

              <FormTextarea
                placeholder="ຫມາຍເຫດທົ່ວໄປ"
                name="notes"
                label="ຫມາຍເຫດທົ່ວໄປ"
                rows={2}
              />

              <div className="space-y-4">
                <h2 className="font-medium text-lg">ລາຍການກວດ</h2>
                {fields.map((field, index) => {
                  const itemMeta = detail.items[index];
                  if (!itemMeta) return null;
                  return (
                    <QcChecklistItemFields
                      key={field.id}
                      index={index}
                      itemMeta={itemMeta}
                      detail={detail}
                    />
                  );
                })}
              </div>

              <div className="sticky bottom-0 z-10 mt-8 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 px-4 py-4 backdrop-blur supports-backdrop-filter:bg-background/80">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    navigate({ to: "/app/qc/inspection", search: { page: 1 } })
                  }
                >
                  ຍົກເລີກ
                </Button>
                <Button
                  type="submit"
                  disabled={submitBusy}
                  className="bg-black text-white hover:bg-black/90"
                >
                  {submitBusy ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກຜົນກວດ"}
                </Button>
              </div>
            </FormRoot>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export function QcInspectionFormPage() {
  const { standardId } = useParams({
    from: "/app/qc/inspection/$standardId",
  });

  const detailQuery = useInspectorStandardDetailQuery(standardId, {
    enabled: Boolean(standardId),
  });
  const workTypesQuery = useWorkItemTypesQuery({ enabled: true });

  if (detailQuery.isLoading) {
    return <QueryState isLoading>ກຳລັງໂຫຼດມາດຕະຖານ...</QueryState>;
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <QueryState isError error={detailQuery.error}>
        ບໍ່ພົບມາດຕະຖານ ຫຼືບໍ່ອະນຸຍາດ
      </QueryState>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <QcInspectionFormInner
          detail={detailQuery.data}
          workTypes={workTypesQuery.data ?? []}
        />
      </Main>
    </>
  );
}
